import produce from "immer";

import activityFormatter, {
  comment as commentFormatter,
  reaction,
  reply as replyFormatter,
} from "lib/feed/activityFormatter";

export default function reducer(state, { type, ...payload }) {
  const newState = produce(state, (draft) => {
    const findFeed = (feedGroup, feedId) => {
      return draft.feeds[`${feedGroup}:${feedId}`];
    };

    const warnNotFound = (resourceType) => {
      console.warn(`${type} not performed. ${resourceType} not found`, payload);
    };

    const addActivity = (activity) => {
      try {
        const formattedActivity = activityFormatter(activity);
        const existing = draft.activities[formattedActivity.id];
        if (existing) {
          Object.assign(existing, formattedActivity);
        } else {
          draft.activities[formattedActivity.id] = formattedActivity;
        }
        return formattedActivity;
      } catch (e) {
        console.error("Unable to add activity", activity, e);
      }
    };

    if (type === "ADD_ACTIVITY") {
      const feed = findFeed(payload.feedGroup, payload.feedId);
      if (feed) {
        payload.activity.forEach((activity) => {
          // add the activity to the state
          const formattedActivity = addActivity(activity);
          if (!formattedActivity) {
            // there was a problem adding the activity. Stop processing
            return;
          }

          // reference in the feed
          const existing = feed.activities.find(
            (a) => a.id === formattedActivity.id
          );

          if (existing) {
            existing.isNew = payload.isNew && existing.isNew; // dont let incoming activity mark existing as new.
          } else {
            const item = {
              id: formattedActivity.id,
              // Mark an item as new if it doesn't belong to the current user
              isNew:
                payload.isNew &&
                payload.feedId !== formattedActivity.author.userId &&
                payload.userId !== formattedActivity.author.userId,
            };

            // Add new posts and new posts by the user to the top
            if (
              item.isNew ||
              (activity?.actor?.id &&
                activity?.actor?.id === payload.userId &&
                (activity.verb === "post" || activity.verb === "poll"))
            ) {
              feed.activities.unshift(item);
            } else {
              feed.activities.push(item);
            }
          }
        });
        feed.hasNew = Boolean(feed.activities.find((a) => a.isNew));
      }
    }

    if (type === "UPDATE_ACTIVITY") {
      const activity = draft.activities[payload.activityId];
      if (!activity) return warnNotFound("activity");
      activity.body = payload.body;
      activity.mediaType = payload.mediaType;
      activity.mediaUrl = payload.mediaUrl;
      activity.edited = true;
      if (payload.postText) {
        activity.item.object.data.postText = payload.postText;
      }
    }

    if (type === "UPDATE_EVENT") {
      const activity = draft.activities[payload.activityId];
      if (!activity) return warnNotFound("activity");

      if (payload?.data?.host?.userId) {
        if (payload?.data?.host?.userId !== activity?.item?.host?.id) {
          activity.item.host = {
            ...activity.item.host,
            id: payload.data.host.userId,
            data: {
              ...payload.data.host,
            },
          };
        }
      } else {
        activity.item.host = null;
      }

      activity.item.object.data = {
        ...activity.item.object.data,
        ...payload.data,
      };

      if (payload?.data?.timezone) {
        activity.item.object.data.timezone = payload.data.timezone;
      }

      activity.item.mediaUrl = payload?.data?.attachment?.url;

      activity.edited = true;
    }

    if (type === "REMOVE_ACTIVITY") {
      const feed = findFeed(payload.feedGroup, payload.feedId);
      const removedActivityIds = payload.activity.map(
        (activity) => activity.id
      );
      const removeActivitiesFilter = (activity) =>
        !removedActivityIds.includes(activity.id);

      if (feed) {
        feed.activities = feed.activities.filter(removeActivitiesFilter);
      } else {
        const feedKeys = Object.keys(draft.feeds);
        feedKeys.forEach((feedKey) => {
          draft.feeds[feedKey].activities = draft.feeds[
            feedKey
          ].activities.filter(removeActivitiesFilter);
        });
      }
    }

    if (type === "UPSERT_FEED") {
      const existing = findFeed(payload.feedGroup, payload.feedId);
      if (!existing) {
        draft.feeds[`${payload.feedGroup}:${payload.feedId}`] = {
          feedGroup: payload.feedGroup,
          feedId: payload.feedId,
          activities: [],
          hasNew: false,
          hasNextPage: false,
        };
      } else {
        Object.assign(existing, payload);
      }
    }

    if (type === "UPDATE_NEW_ACTIVITY") {
      const feed = findFeed(payload.feedGroup, payload.feedId);
      if (!feed) return warnNotFound("feed");
      feed.activities.forEach((i) => {
        i.isNew = false;
      });
      feed.hasNew = false;
    }

    if (type === "ADD_ACTIVITY_REACTION") {
      const activity = draft.activities[payload.activityId];
      if (!activity) return warnNotFound("activity");
      const currentCount = activity.reactionCounts[payload.reaction.kind] || 0;
      activity.reactionCounts[payload.reaction.kind] = currentCount + 1;
      activity.reactions.push(reaction(payload.reaction));
    }

    if (type === "REPLACE_TEMP_ACTIVITY_REACTION") {
      const activity = draft.activities[payload.activityId];
      if (!activity) return warnNotFound("activity");

      const tempReactionIndex = activity.reactions.findIndex(
        (r) => r.item.temp
      );
      if (tempReactionIndex === -1) return warnNotFound("reaction");
      activity.reactions[tempReactionIndex] = reaction(payload.reaction);
    }

    if (type === "REMOVE_ACTIVITY_REACTION") {
      const activity = draft.activities[payload.activityId];
      if (!activity) return warnNotFound("activity");
      const currentCount = activity.reactionCounts[payload.reaction.kind];
      activity.reactionCounts[payload.reaction.kind] = currentCount - 1;
      activity.reactions = activity.reactions.filter(
        (r) => r.id !== payload.reaction.id
      );
    }

    if (type === "ADD_NEW_COMMENT") {
      const activity = draft.activities[payload.activityId];
      if (!activity) return warnNotFound("activity");
      activity.commentCount++;
      activity.comments.push(commentFormatter(payload.comment));
    }

    if (type === "ADD_NEW_ANSWER") {
      const activity = draft.activities[payload.activityId];
      if (!activity) return warnNotFound("activity");
      activity.answerCount++;
      activity.answers.push(payload.answer);
      activity.ownAnswers.push(payload.answer);
    }

    if (type === "ADD_ANSWERS") {
      const activity = draft.activities[payload.activityId];
      if (!activity) return warnNotFound("activity");
      payload.answers.forEach((item) => {
        const formattedAnswer = commentFormatter(item);
        activity.answers.unshift(formattedAnswer);
      });
    }

    if (type === "ADD_COMMENT_REACTION") {
      const activity = draft.activities[payload.activityId];
      if (!activity) return warnNotFound("activity");
      const comment = activity.comments.find((c) => c.id === payload.commentId);
      if (!comment) return warnNotFound("comment");

      const currentCount = comment.reactionCounts[payload.reaction.kind] || 0;
      comment.reactionCounts[payload.reaction.kind] = currentCount + 1;
      comment.reactions.push(reaction(payload.reaction));
    }

    if (type === "REPLACE_TEMP_COMMENT_REACTION") {
      const activity = draft.activities[payload.activityId];
      if (!activity) return warnNotFound("activity");
      const comment = activity.comments.find((c) => c.id === payload.commentId);
      if (!comment) return warnNotFound("comment");

      const tempReactionIndex = comment.reactions.findIndex((r) => r.item.temp);
      if (tempReactionIndex === -1) return warnNotFound("reaction");
      comment.reactions[tempReactionIndex] = reaction(payload.reaction);
    }

    if (type === "REMOVE_COMMENT_REACTION") {
      const activity = draft.activities[payload.activityId];
      if (!activity) return warnNotFound("activity");
      const comment = activity.comments.find((c) => c.id === payload.commentId);
      if (!comment) return warnNotFound("comment");

      const currentCount = comment.reactionCounts[payload.reaction.kind];
      comment.reactionCounts[payload.reaction.kind] = currentCount - 1;
      comment.reactions = comment.reactions.filter(
        (r) => r.id !== payload.reaction.id
      );
    }

    if (type === "ADD_COMMENTS") {
      const activity = draft.activities[payload.activityId];
      if (!activity) return warnNotFound("activity");
      payload.comments.forEach((item) => {
        const index = activity.comments.find((i) => i.id === item.id);
        if (index > -1) {
          activity.comments[index] = commentFormatter(item);
        } else {
          activity.comments.push(commentFormatter(item));
        }
      });
    }

    if (type === "UPDATE_COMMENT") {
      const activity = draft.activities[payload.activityId];
      if (!activity) return warnNotFound("activity");
      activity.comments = activity.comments.map((item) => {
        if (item.id === payload.commentId) {
          item.body = payload.body;
          item.edited = true;
          item.mediaType = payload.mediaType;
          item.mediaUrl = payload.mediaUrl ?? payload.url;
        }
        return item;
      });
    }

    if (type === "UPDATE_REPLY") {
      const activity = draft.activities[payload.activityId];
      if (!activity) return warnNotFound("activity");
      activity.comments = activity.comments.map((item) => {
        if (item.id === payload.parentCommentId) {
          item.replies.map((reply) => {
            if (reply.id === payload.commentId) {
              reply.body = payload.body;
              reply.edited = true;
              reply.mediaType = payload.mediaType;
              reply.mediaUrl = payload.mediaUrl ?? payload.url;
            }
            return reply;
          });
        }
        return item;
      });
    }

    if (type === "ADD_NEW_REPLY") {
      const activity = draft.activities[payload.activityId];
      if (!activity) return warnNotFound("activity");
      const comment = activity.comments.find((c) => c.id === payload.commentId);
      if (comment) {
        comment.replyCount++;
        comment.replies.push(replyFormatter(payload.reply));
      }
    }

    if (type === "ADD_REPLY_REACTION") {
      const activity = draft.activities[payload.activityId];
      if (!activity) return warnNotFound("activity");
      const comment = activity.comments.find((c) => c.id === payload.commentId);
      if (!comment) return warnNotFound("comment");
      const reply = comment.replies.find((r) => r.id === payload.replyId);
      if (!reply) return warnNotFound("reply");

      const currentCount = reply.reactionCounts[payload.reaction.kind] || 0;
      reply.reactionCounts[payload.reaction.kind] = currentCount + 1;
      reply.reactions.push(reaction(payload.reaction));
    }

    if (type === "REPLACE_TEMP_REPLY_REACTION") {
      const activity = draft.activities[payload.activityId];
      if (!activity) return warnNotFound("activity");
      const comment = activity.comments.find((c) => c.id === payload.commentId);
      if (!comment) return warnNotFound("comment");
      const reply = comment.replies.find((r) => r.id === payload.replyId);
      if (!reply) return warnNotFound("reply");

      const tempReactionIndex = reply.reactions.findIndex((r) => r.item.temp);
      if (tempReactionIndex === -1) return warnNotFound("reaction");
      reply.reactions[tempReactionIndex] = reaction(payload.reaction);
    }

    if (type === "REMOVE_REPLY_REACTION") {
      const activity = draft.activities[payload.activityId];
      if (!activity) return warnNotFound("activity");

      const comment = activity.comments.find((c) => c.id === payload.commentId);
      const reply = comment.replies.find((r) => r.id === payload.replyId);
      const currentCount = reply.reactionCounts[payload.reaction.kind];
      reply.reactionCounts[payload.reaction.kind] = currentCount - 1;
      reply.reactions = reply.reactions.filter(
        (r) => r.id !== payload.reaction.id
      );
    }

    if (type === "ADD_REPLIES") {
      const activity = draft.activities[payload.activityId];
      if (!activity) return warnNotFound("activity");
      const comment = activity.comments.find((c) => c.id === payload.commentId);
      payload.replies.forEach((item) => {
        const index = comment.replies.find((i) => i.id === item.id);
        if (index > -1) {
          comment.replies[index] = replyFormatter(item);
        } else {
          comment.replies.push(replyFormatter(item));
        }
      });
    }

    if (type === "PAGE_INFO") {
      const feed = findFeed(payload.feedGroup, payload.feedId);
      if (!feed) return warnNotFound("activity");
      feed.hasNextPage = payload.hasNext;
    }

    if (type === "ADD_FOLLOWER_IDS") {
      const feed = findFeed(payload.feedGroup, payload.feedId);
      if (!feed) return warnNotFound("activity");
      feed.followerIds = payload.followerIds;
      feed.followingIds = payload.followingIds;
      feed.followStats = payload.followStats;
    }

    if (type === "UPDATE_FOLLOWERS_IDS") {
      const feed = findFeed(payload.feedGroup, payload.feedId);
      if (!feed) return warnNotFound("activity");
      feed.followerIds = [...feed.followerIds, ...payload.followerIds];
    }

    if (type === "ADD_ACTIVITY_VOTE") {
      const activity = draft.activities[payload.activityId];
      if (!activity) return warnNotFound("activity");
      const currentCount = activity.votesCount || 0;
      activity.votesCount = currentCount + 1;
      activity.votes.push(reaction(payload.vote));
      activity.ownVotes.push(reaction(payload.vote));

      const selectedOption = payload?.vote?.data?.choices?.[0];
      const optionIndex =
        activity?.item?.object?.data?.options.findIndex(
          (opt) => opt === selectedOption
        ) ??
        activity?.item?.activities?.[0]?.object?.data?.options.findIndex(
          (opt) => opt === selectedOption
        );

      if (activity.item.activities) {
        activity.item.activities[0].object.data.results[optionIndex] =
          +(
            activity?.item?.activities?.[0]?.object?.data?.results[
              optionIndex
            ] ?? 0
          ) + 1;
      } else {
        activity.item.object.data.results[optionIndex] =
          +(activity?.item?.object?.data?.results[optionIndex] ?? 0) + 1;
      }
    }

    if (type === "SWITCH_ACTIVITY_VOTE") {
      const activity = draft.activities[payload.activityId];
      if (!activity) return warnNotFound("activity");
      activity.votes = activity.votes.filter(
        (r) => r.id !== payload.voteToRemove.id
      );
      activity.votes.push(reaction(payload.voteToAdd));
      activity.ownVotes = [reaction(payload.voteToAdd)];

      const selectedOptionToRemove =
        payload?.voteToRemove?.item?.data?.choices?.[0];
      const optionIndexToRemove =
        activity?.item?.object?.data?.options.findIndex(
          (opt) => opt === selectedOptionToRemove
        ) ??
        activity?.item?.activities?.[0]?.object?.data?.options.findIndex(
          (opt) => opt === selectedOptionToRemove
        );

      if (activity.item.activities) {
        activity.item.activities[0].object.data.results[optionIndexToRemove] =
          +(
            activity?.item?.activities?.[0]?.object?.data?.results[
              optionIndexToRemove
            ] ?? 0
          ) - 1;
      } else {
        activity.item.object.data.results[optionIndexToRemove] =
          +(activity?.item?.object?.data?.results[optionIndexToRemove] ?? 0) -
          1;
      }

      const selectedOptionToAdd = payload?.voteToAdd?.data?.choices?.[0];
      const optionToAddIndex =
        activity?.item?.object?.data?.options.findIndex(
          (opt) => opt === selectedOptionToAdd
        ) ??
        activity?.item?.activities?.[0]?.object?.data?.options.findIndex(
          (opt) => opt === selectedOptionToAdd
        );

      if (activity.item.activities) {
        activity.item.activities[0].object.data.results[optionToAddIndex] =
          +(
            activity?.item?.activities[0]?.object?.data?.results[
              optionToAddIndex
            ] ?? 0
          ) + 1;
      } else {
        activity.item.object.data.results[optionToAddIndex] =
          +(activity?.item?.object?.data?.results[optionToAddIndex] ?? 0) + 1;
      }
    }

    if (type === "REPLACE_TEMP_ACTIVITY_VOTE") {
      const activity = draft.activities[payload.activityId];
      if (!activity) return warnNotFound("activity");

      const tempVoteIndex = activity.votes.findIndex((r) => r.item.temp);
      if (tempVoteIndex === -1) return warnNotFound("vote");
      activity.votes[tempVoteIndex] = reaction(payload.vote);

      const tempOwnVoteIndex = activity.ownVotes.findIndex((r) => r.item.temp);
      if (tempOwnVoteIndex === -1) return warnNotFound("vote");
      activity.ownVotes[tempOwnVoteIndex] = reaction(payload.vote);
    }

    if (type === "REMOVE_ACTIVITY_VOTE") {
      const activity = draft.activities[payload.activityId];
      if (!activity) return warnNotFound("activity");

      const currentCount = activity.votesCount;
      activity.votesCount = currentCount - 1;
      activity.votes = activity.votes.filter((r) => r.id !== payload.vote.id);
      activity.ownVotes = [];

      const selectedOption = payload?.vote?.item?.data?.choices?.[0];
      const optionIndex =
        activity?.item?.object?.data?.options.findIndex(
          (opt) => opt === selectedOption
        ) ??
        activity?.item?.activities?.[0]?.object?.data?.options.findIndex(
          (opt) => opt === selectedOption
        );

      if (activity.item.activities) {
        activity.item.activities[0].object.data.results[optionIndex] =
          +(
            activity?.item?.activities?.[0]?.object?.data?.results[
              optionIndex
            ] ?? 0
          ) - 1;
      } else {
        activity.item.object.data.results[optionIndex] =
          +(activity?.item?.object?.data?.results[optionIndex] ?? 0) - 1;
      }
    }

    if (type === "DELETE_ACTIVITY_POLL") {
      const activity = draft.activities[payload.activityId];
      if (!activity) return warnNotFound("activity");
      if (activity?.item?.object?.data) {
        activity.item.object.data.deleted = true;
      }
    }
  });

  return newState;
}
